import React from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';

/** Branded mini icons matching Claude Connectors style */
const ConnectorIcon = ({ id }) => {
    if (id === 'adobe_target') {
        return (
            <div
                className="w-5 h-5 rounded-md bg-[#eb1000] flex items-center justify-center text-white shrink-0 shadow-xs"
                title="Adobe Target"
            >
                <svg
                    className="w-3.2 h-3.2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                </svg>
            </div>
        );
    }
    if (id === 'adobe_analytics') {
        return (
            <div
                className="w-5 h-5 rounded-md bg-[#ea380c] flex items-center justify-center text-white shrink-0 shadow-xs"
                title="Adobe Analytics"
            >
                <svg
                    className="w-3.2 h-3.2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M3 3v18h18" />
                    <path d="m19 9-5 5-4-4-3 3" />
                </svg>
            </div>
        );
    }
    if (id === 'tealium') {
        return (
            <div
                className="w-5 h-5 rounded-md bg-[#00828a] flex items-center justify-center text-white shrink-0 shadow-xs font-bold text-[10px]"
                title="Tealium"
            >
                T
            </div>
        );
    }
    if (id === 'chrome_devtools') {
        return (
            <div
                className="w-5 h-5 rounded-md bg-[#1a73e8] flex items-center justify-center text-white shrink-0 shadow-xs"
                title="Chrome DevTools"
            >
                <svg
                    className="w-3.2 h-3.2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20" />
                </svg>
            </div>
        );
    }
    return (
        <div
            className="w-5 h-5 rounded-md bg-gray-600 flex items-center justify-center text-white shrink-0 shadow-xs font-bold text-[10px]"
            title={id}
        >
            {id?.charAt(0)?.toUpperCase() || 'M'}
        </div>
    );
};

/**
 * ConnectorsPicker: Claude-inspired dropdown allowing users to toggle
 * which MCP connectors are active for their queries.
 */
const ConnectorsPicker = ({
    connectors = [],
    selectedConnectors = [],
    onToggleConnector,
    disabled = false,
}) => {
    const {
        ref,
        isComponentVisible,
        setIsComponentVisible,
    } = useComponentVisible(false);

    const availableConnectors = connectors;
    const activeCount = selectedConnectors.length;

    const handleToggle = (id) => {
        if (disabled) return;
        // Don't disable the last active connector
        if (selectedConnectors.includes(id) && activeCount === 1) return;
        onToggleConnector?.(id);
    };

    return (
        <div ref={ref} className="relative z-[1001]">
            <button
                type="button"
                id="ai-connectors-trigger"
                className="sai-connectors-trigger text-gray-800 bg-white hover:bg-gray-50 focus:outline-none rounded-lg border border-gray-300 text-xs font-medium px-3 py-1.5 flex items-center justify-between gap-2 min-w-[135px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-xs"
                title="Connectors"
                aria-label="Manage MCP Connectors"
                aria-expanded={isComponentVisible}
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsComponentVisible(!isComponentVisible);
                }}
            >
                <span className="flex items-center gap-1.5 font-semibold text-gray-700">
                    <SlidersHorizontal size={13} className="text-primary shrink-0" aria-hidden="true" />
                    <span>Connectors</span>
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary">
                        {activeCount}
                    </span>
                    <ChevronDown
                        size={13}
                        className={`text-gray-400 transition-transform duration-200 shrink-0 ${
                            isComponentVisible ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                    />
                </span>
            </button>

            {isComponentVisible && (
                <div
                    id="ai-connectors-dropdown"
                    className="bg-white rounded-xl shadow-xl border border-gray-200 absolute top-full right-0 mt-1.5 w-[280px] z-[1002] overflow-hidden select-none animate-in fade-in zoom-in-95 duration-100"
                    role="dialog"
                    aria-label="Connectors menu"
                >
                    <div className="px-3.5 py-2.5 flex items-center justify-between border-b border-gray-100 bg-gray-50/70">
                        <span className="text-xs font-semibold text-gray-800">Connectors</span>
                        <span className="text-[11px] text-gray-500 font-medium">
                            {activeCount} of {availableConnectors.length} active
                        </span>
                    </div>

                    <ul className="py-1 divide-y divide-gray-50">
                        {availableConnectors.map((connector, index) => {
                            const isActive = selectedConnectors.includes(connector.id);
                            const isOnlyActive = isActive && activeCount === 1;

                            return (
                                <li key={connector.id} id={index === 0 ? "ai-connector-item-0" : undefined}>
                                    <div
                                        className={`w-full px-3.5 py-2.5 flex items-center justify-between gap-3 text-left transition-colors ${
                                            isOnlyActive ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
                                        }`}
                                        onClick={() => handleToggle(connector.id)}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <ConnectorIcon id={connector.id} />
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-xs font-semibold text-gray-800 truncate">
                                                    {connector.label}
                                                </span>
                                                {connector.description && (
                                                    <span className="text-[10px] text-gray-400 truncate max-w-[170px]" title={connector.description}>
                                                        {connector.description}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Claude-style toggle switch */}
                                        <button
                                            type="button"
                                            id={index === 0 ? "ai-connector-toggle-0" : undefined}
                                            role="switch"
                                            aria-checked={isActive}
                                            aria-label={`Toggle ${connector.label}`}
                                            disabled={disabled || isOnlyActive}
                                            title={isOnlyActive ? 'At least one connector must remain active' : undefined}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggle(connector.id);
                                            }}
                                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                                                isActive ? 'bg-[#2563eb]' : 'bg-gray-300'
                                            } ${isOnlyActive ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                                                    isActive ? 'translate-x-4' : 'translate-x-0'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ConnectorsPicker;

